import React, { useEffect, useMemo, useState } from "react";
import "./ExpiredTickets.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const LETTERS_URL = "https://www.lianfoong.com/api/pawn-transactions/forfeiture-letters/by-mmyy";
const LOCK_URL    = "https://www.lianfoong.com/api/pawn-transactions/forfeitures/lock-batch";
const BUCKETS_URL = "https://www.lianfoong.com/api/pawn-transactions/expired-buckets";
const REPORT_URL  = "https://www.lianfoong.com/api/pawn-transactions/forfeitures/report/by-mmyy";

export default function ExpiredTickets() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expired, setExpired] = useState([]);
  const [lapsed, setLapsed]   = useState([]);
  const [letterMMYY, setLetterMMYY] = useState("");
  const [lockMMYY, setLockMMYY]     = useState("");
  const [csvMMYY, setCsvMMYY]       = useState("");      // <-- NEW: MMYY for CSV
  const [busy, setBusy] = useState(false);
  useEscapeToGoBack();

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await fetch(BUCKETS_URL);
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        setExpired(j.expired || []);
        setLapsed(j.lapsed || []);
      } catch (e) {
        setErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshBuckets = async () => {
    const r = await fetch(BUCKETS_URL);
    const j = await r.json();
    setExpired(j.expired || []);
    setLapsed(j.lapsed || []);
  };

  const validateMMYY = (v) => /^\d{4}$/.test(v);

  const onGenerateLetters = () => {
    const v = letterMMYY.trim();
    if (!validateMMYY(v)) { alert("Enter MMYY (e.g. 0425)."); return; }
    window.open(`${LETTERS_URL}?mmyy=${encodeURIComponent(v)}`, "_blank");
  };

  function extractReceiptMMYY(receiptNo) {
  const s = String(receiptNo || "").toUpperCase().trim();
  const m1 = s.match(/\/(\d{4})\//);     // between slashes
  if (m1) return m1[1];
  const m2 = s.match(/\b(\d{4})\d{3,}\b/); // 4 digits followed by more (S-type)
  if (m2) return m2[1];
  return "";
}

  const onBatchLock = async () => {
    const v = lockMMYY.trim();
    if (!/^\d{4}$/.test(v)) { alert("Enter MMYY (e.g. 0425)."); return; }
    if (!window.confirm(`Lock ALL tickets for ${v.slice(0,2)}/${v.slice(2)}? This cannot be undone.`)) return;

    try {
      setBusy(true);
      const r = await fetch(`${LOCK_URL}/${encodeURIComponent(v)}`, { method: "POST" });
      if (!r.ok) throw new Error(await r.text());

      await refreshBuckets();
      alert("Tickets locked.");

      // open the printable report in a new tab
      window.open(`${REPORT_URL}?mmyy=${encodeURIComponent(v)}`, "_blank");
    } catch (e) {
      alert(e.message || "Failed to lock tickets.");
    } finally {
      setBusy(false);
    }
  };

  // ---- NEW: CSV download ----
  const matchMMYY = (isoDate, mmyy) => {
    if (!isoDate || !mmyy || !/^\d{4}$/.test(mmyy)) return false;
    const m = String(isoDate).match(/^(\d{4})-(\d{2})/); // yyyy-mm
    if (!m) return false;
    const yearYY  = m[1].slice(2); // last 2 of year
    const monthMM = m[2];
    return monthMM === mmyy.slice(0,2) && yearYY === mmyy.slice(2);
  };

  const toCsv = (rows) => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = [
      { label: "Bucket",     get: (r) => r._bucket || "" },
      { label: "Receipt",    get: (r) => r.receiptNo || "" },
      { label: "Grant Date", get: (r) => r.grantDate || "" },
      { label: "Expiry Date",get: (r) => r.expiryDate || "" },
      { label: "Amount",     get: (r) => (Number(r.amount || 0)).toFixed(2) },
      { label: "Item",       get: (r) => r.item || "" },
      { label: "Customer",   get: (r) => r.customerName || "" },
      { label: "Mobile",     get: (r) => r.customerMobile || "" },
    ];
    const head = headers.map(h => escape(h.label)).join(",");
    const body = rows.map(r => headers.map(h => escape(h.get(r))).join(",")).join("\r\n");
    return `${head}\r\n${body}`;
  };

  const onDownloadCsv = () => {
    const v = csvMMYY.trim();
    if (!validateMMYY(v)) { alert("Enter MMYY (e.g. 1025)."); return; }

    // Tag which bucket each row came from, combine, then filter by MMYY on expiryDate.
    const taggedExpired = expired.map(r => ({ ...r, _bucket: "Expired" }));
    const taggedLapsed  = lapsed.map(r  => ({ ...r, _bucket: "Lapsed" }));
    const all = [...taggedExpired, ...taggedLapsed];
    const rows = all.filter(r => extractReceiptMMYY(r.receiptNo) === v);

    if (!rows.length) {
      alert(`No tickets found for ${v.slice(0,2)}/${v.slice(2)}.`);
      return;
    }

    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `expired-tickets-${v}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  // --------------------------

  const totals = useMemo(() => {
    const sum = (rows) => rows.reduce((a, r) => a + Number(r.amount || 0), 0);
    return {
      expiredCount: expired.length,
      expiredAmount: sum(expired),
      lapsedCount:  lapsed.length,
      lapsedAmount: sum(lapsed),
    };
  }, [expired, lapsed]);

  if (loading) {
    return (
      <div className="xt-page">
        <div className="xt-wrap">
          <div className="xt-card">Loading…</div>
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="xt-page">
        <div className="xt-wrap">
          <div className="xt-card xt-error">Error: {err}</div>
        </div>
      </div>
    );
  }

  const Bucket = ({ title, rows }) => (
    <details className="xt-acc">
      <summary className="xt-acc__summary">
        <span className="xt-acc__title">{title}</span>
        <span className="xt-acc__meta">
          <span className="xt-acc__count">{rows.length}</span>
          <span className="xt-acc__sep">•</span>
          <span className="xt-acc__amt">
            {fmtCurrency(rows.reduce((a,r)=>a+Number(r.amount||0),0))}
          </span>
        </span>
      </summary>

      <div className="xt-card xt-acc__panel">
        <div className="xt-tableWrap">
          <table className="xt-table">
            <thead>
              <tr>
                <th className="col-receipt">Receipt</th>
                <th className="col-expiry">Expiry (dd-mm-yyyy)</th>
                <th className="col-item">Item</th>
                <th className="col-amount xt-right">Amount</th>
                <th className="col-customer">Customer</th>
                <th className="col-mobile">Mobile</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="col-receipt">{r.receiptNo}</td>
                  <td className="col-expiry">{fmtDate(r.expiryDate)}</td>
                  <td className="col-item">{r.item}</td>
                  <td className="col-amount xt-right">{fmtCurrency(r.amount)}</td>
                  <td className="col-customer">{r.customerName || ""}</td>
                  <td className="col-mobile">{r.customerMobile || ""}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="xt-empty">None</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );

  return (
    <div className="xt-page">
      <div className="xt-wrap xt-wide">
        <div className="xt-card">
          <div className="xt-title">Expired Tickets</div>
          <div className="xt-note">
            • “Expired” = past expiry but within 3 months.&nbsp;&nbsp;• “Lapsed (≥ 3 months)”
          </div>
        </div>

        {/* Batch controls */}
        <div className="xt-card" style={{marginBottom: 16}}>
          <div
            className="xt-controls"
            style={{display:"grid", gap:12, gridTemplateColumns:"1fr auto auto"}}
          >
            {/* Letters */}
            <div style={{display:"contents"}}>
              <label style={{alignSelf:"center"}} htmlFor="letters-mmyy">Reminder Letters (MMYY)</label>
              <input
                id="letters-mmyy"
                inputMode="numeric"
                pattern="\d{4}"
                placeholder="e.g. 0425"
                maxLength={4}
                value={letterMMYY}
                onChange={(e)=>setLetterMMYY(e.target.value.replace(/\D/g,"").slice(0,4))}
              />
              <button className="xt-btn" onClick={onGenerateLetters} disabled={busy}>
                Generate Reminder Letters
              </button>
            </div>

            {/* Lock */}
            <div style={{display:"contents"}}>
              <label style={{alignSelf:"center"}} htmlFor="lock-mmyy">Lock Tickets (MMYY)</label>
              <input
                id="lock-mmyy"
                inputMode="numeric"
                pattern="\d{4}"
                placeholder="e.g. 0425"
                maxLength={4}
                value={lockMMYY}
                onChange={(e)=>setLockMMYY(e.target.value.replace(/\D/g,"").slice(0,4))}
              />
              <button className="xt-btn xt-danger" onClick={onBatchLock} disabled={busy}>
                Lock Tickets
              </button>
            </div>

            {/* NEW: CSV download */}
            <div style={{display:"contents"}}>
              <label style={{alignSelf:"center"}} htmlFor="csv-mmyy">Download CSV (MMYY)</label>
              <input
                id="csv-mmyy"
                inputMode="numeric"
                pattern="\d{4}"
                placeholder="e.g. 1025"
                maxLength={4}
                value={csvMMYY}
                onChange={(e)=>setCsvMMYY(e.target.value.replace(/\D/g,"").slice(0,4))}
              />
              <button className="xt-btn" onClick={onDownloadCsv} disabled={busy}>
                Download CSV
              </button>
            </div>
          </div>
        </div>

        <Bucket title={`Expired (< 3 months past expiry)`} rows={expired} />
        <Bucket title={`Lapsed (≥ 3 months past expiry)`} rows={lapsed} />
      </div>
    </div>
  );
}

/* helpers */
function fmtDate(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}
function fmtCurrency(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-SG", { style: "currency", currency: "SGD", minimumFractionDigits: 2 });
}
